'use client';

import { useState } from 'react';
import {
  getEmailValidationError,
  getMessageValidationError,
  getNameValidationError,
} from './footerContactValidation';
import { isValidEmail } from './footerModels';

interface ContactFieldConfig {
  getError: (value: string) => string | null;
  shouldClearError: (value: string) => boolean;
}

interface ContactFieldController {
  value: string;
  error: string | null;
  setValue: (value: string) => void;
  reset: () => void;
  validate: () => boolean;
}

export interface FooterContactFields {
  name: string;
  email: string;
  message: string;
  nameError: string | null;
  emailError: string | null;
  messageError: string | null;
  handleNameChange: (value: string) => void;
  handleEmailChange: (value: string) => void;
  handleMessageChange: (value: string) => void;
  resetContactFormFields: () => void;
  validateName: () => boolean;
  validateEmail: () => boolean;
  validateMessage: () => boolean;
}

function useContactField({
  getError,
  shouldClearError,
}: ContactFieldConfig): ContactFieldController {
  const [value, setFieldValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const setValue = (nextValue: string): void => {
    setFieldValue(nextValue);
    if (error && shouldClearError(nextValue)) {
      setError(null);
    }
  };

  const reset = (): void => {
    setFieldValue('');
    setError(null);
  };

  const validate = (): boolean => {
    const nextError = getError(value);
    if (!nextError) {
      setError(null);
      return true;
    }
    setFieldValue('');
    setError(nextError);
    return false;
  };

  return {
    value,
    error,
    setValue,
    reset,
    validate,
  };
}

export function useFooterContactFields(): FooterContactFields {
  const name = useContactField({
    getError: (value) => getNameValidationError({ name: value }),
    shouldClearError: (value) => value.length > 0,
  });
  const email = useContactField({
    getError: (value) => getEmailValidationError({ email: value }),
    shouldClearError: (value) => isValidEmail({ email: value }),
  });
  const message = useContactField({
    getError: (value) => getMessageValidationError({ message: value }),
    shouldClearError: (value) => value.length > 0,
  });

  const resetContactFormFields = (): void => {
    name.reset();
    email.reset();
    message.reset();
  };

  return {
    name: name.value,
    email: email.value,
    message: message.value,
    nameError: name.error,
    emailError: email.error,
    messageError: message.error,
    handleNameChange: name.setValue,
    handleEmailChange: email.setValue,
    handleMessageChange: message.setValue,
    resetContactFormFields,
    validateName: name.validate,
    validateEmail: email.validate,
    validateMessage: message.validate,
  };
}
